import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { add, addMonths, format } from 'date-fns';
import moment from 'moment-timezone';
import * as TzLookup from 'tz-lookup';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DateTimeInterface } from '~/component/form/datetime/interfaces/datetime.interface';
import { ValueAccessorBase } from '~/component/value-accessor.base';

import { SuccessStatusEnum } from '~/core/enums/success-status.enum';
import { parseISO8601Duration } from '~/core/helpers/date/parse-iso-duration/parse-iso-duration.helper';
import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { dateRangeValidator } from '~/core/validators/date/date-range.validator';
import { ActivityPlanningDateSelectionInterface } from '~/organizer/activity-planning/components/shared/date-selection/interfaces/date-selection.interface';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { repeatUntil } from '~/core/operators/repeat-until.operator';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { LocaleService } from '~/i18n/services/locale.service';

@Component({
  selector: 'app-organizer-timeslot-edition-date',
  templateUrl: './date.component.html',
})
export class OrganizerTimeslotEditionDateComponent
  extends ValueAccessorBase<ActivityPlanningDateSelectionInterface>
  implements OnInit, OnDestroy
{
  private readonly recurrentDurationMonths = 3;
  private formGroupSubscription: Subscription;
  private startDateTimeSubscription: Subscription;
  private readonly subscriptions = new Subscription();

  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;

  public setDateValueAttempt = 0;
  public activity: Activity;
  public formGroup: FormGroup;
  public isLastTimeslot: boolean;
  public isOverlayDisplayed = false;
  public overlayStatus = SuccessStatusEnum.LOADING;
  public previousUrl: string;
  public timeslot: Timeslot;
  public endDateMax = new Date();
  public endDateMin = new Date();
  public readonly startDateMin = new Date();
    constructor(
    protected readonly formBuilder: FormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly activityService: OrganizerSharedActivityService,
    private readonly translateService: TranslateService,
    private readonly localeService: LocaleService,
    public readonly paramethorService: ParamethorService
  ) {
    super();
  }

  public ngOnDestroy(): void {
    this.startDateTimeSubscription.unsubscribe();
    this.formGroupSubscription.unsubscribe();
  }

  public ngOnInit(): void {
    const { timeslotData } = this.activatedRoute.snapshot.data;
    const startDate = new Date(timeslotData.timeslot.startDate);

    this.activity = timeslotData.activity;
    this.timeslot = timeslotData.timeslot;

    this.previousUrl = `/organizer.timeslot.manage/${timeslotData.timeslot.identifier}`;

    this.buildFormGroup(startDate);

    this.subscriptions.add(
      this.activityService
        .getTimeslots({
          activityIdentifier: timeslotData.activity.sku,
          startDate: timeslotData.timeslot.startDate,
        })
        .subscribe((response) => {
          this.isLastTimeslot = response?.items?.length === 1;
        })
    );
  }

  public get dateLabel(): string {
    return this.isLive
      ? this.translateService.instant(
          'activity_planning.date_selection.start_date'
        )
      : this.translateService.instant(
          'activity_planning.date_selection.local_start_date'
        );
  }

  public get isLive(): boolean {
    return this.activity.isLive;
  }

  private buildFormGroup(startDate: number | Date): void {
    this.formGroup = this.formBuilder.group({
      startDateTime: [{}],
      endDate: [''],
    });

    this.startDateTimeSubscription = this.formGroup
      .get('startDateTime')
      .valueChanges.subscribe((startDateTime: DateTimeInterface) => {
        this.endDateMin = new Date(startDateTime.date);
        this.endDateMin.setDate(this.endDateMin.getDate() + 1);

        this.endDateMax = new Date(startDateTime.date);
        this.endDateMax.setMonth(
          this.endDateMax.getMonth() + Number(this.recurrentDurationMonths)
        );
        this.setEndDateValidator(false);
      });

    this.formGroupSubscription = this.formGroup.valueChanges.subscribe(() => {
      this.setDateSelectionValue();
    });

    this.formGroup.setValue({
      startDateTime: {
        date: format(startDate, 'yyyy-MM-dd'),
        time: this.formattedStartTime(startDate),
        timezone: this.isLive
          ? moment?.tz.guess()
          : TzLookup(
              this.activity?.location?.geo?.latitude,
              this.activity?.location?.geo?.longitude
            ),
      },
      endDate: '',
    });

    this.formGroup.updateValueAndValidity();
  }

  public get date(): FormControl {
    return this.formGroup.get('date') as FormControl;
  }

  public get defaultDateTime(): DateTimeInterface {
    return {
      date: moment(this.timeslot?.startDate).format('yyyy-MM-DD'),
      time: this.formattedStartTime(this.timeslot?.startDate),
      timezone: this.isLive
        ? moment?.tz.guess()
        : TzLookup(
            this.activity?.location?.geo?.latitude,
            this.activity?.location?.geo?.longitude
          ),
    };
  }

  public get isTimezoneSelectionEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.TimezoneSelection);
  }

  public get maxDate(): string {
    return this.formatDate(addMonths(new Date(), 3));
  }

  public get minDate(): string {
    return this.formatDate(new Date());
  }

  public get time(): FormControl {
    return this.formGroup.get('time') as FormControl;
  }

  private getFormValue(key: string) {
    return this.formGroup.get(key).value;
  }

  private formatDate(date: Date): string {
    return format(date, 'yyyy-MM-dd');
  }

  private formattedStartTime(date): string {
    if (this.isTimezoneSelectionEnabled && this.isLive) {
      return format(new Date(date), 'HH:mm', {
        locale: this.localeService.dateLocale,
      });
    }

    const hourSet = this.setHours(
      new Date(this.timeslot?.startDate),
      this.timeslot?.startDateTimezone
    );

    return `${hourSet.getUTCHours().toString()}:${new Date(this.timeslot?.startDate).getUTCMinutes().toString().padStart(2, '0')}`;
  }

  private setDateSelectionValue(): void {
    const {
      date: startDate,
      time: startTime,
      timezone,
    }: DateTimeInterface = this.getFormValue('startDateTime');

    this.value = {
      startDate,
      startTime,
      recurrent: null,
      timezone,
    };
  }

  private setEndDateValidator(isShow: boolean): void {
    const endDateForm = this.formGroup.get('endDate');
    if (isShow) {
      endDateForm.setValidators([
        Validators.required,
        dateRangeValidator(this.endDateMin, this.endDateMax),
      ]);
    } else {
      endDateForm.clearValidators();
    }
    endDateForm.updateValueAndValidity();
  }

  private setHours(date: Date, hours) {
    date.setUTCHours(date.getUTCHours() + hours);
    return date;
  }

  public validateForm(): void {
    this.isOverlayDisplayed = true;

    const newStartDate = moment
      .tz(
        moment(this.formGroup.value.startDateTime.date).format('yyyy-MM-DD') +
          ' ' +
          this.formGroup.value.startDateTime.time,
        this.formGroup.value.startDateTime.timezone
      )
      .format();

    const partialBody = {
      startDate: newStartDate,
      endDate: moment
        .tz(
          add(
            new Date(newStartDate),
            parseISO8601Duration(this.activity.duration).duration
          ).toISOString(),
          this.formGroup.value.startDateTime.timezone
        )
        .format(),
    };

    this.subscriptions.add(
      this.activityService
        .patchTimeslot(this.timeslot.identifier, partialBody)
        .pipe(
          mergeMap(() => {
            return this.activityService
              .getTimeslot(this.timeslot.identifier)
              .pipe(
                repeatUntil(
                  (timeslot) =>
                    new Date(timeslot.startDate).toString() ===
                    new Date(partialBody.startDate).toString()
                )
              );
          })
        )
        .subscribe(() => {
          this.overlayStatus = SuccessStatusEnum.SUCCESS;
        })
    );
  }
}
